import { Button } from '@studio/design-system/button';
import { motion, Motions } from '@studio/design-system/motion';
import { Notice } from '@studio/design-system/notification';
import { Content, Count, List, Tabs, Trigger } from '@studio/design-system/tabs';
import { formatNow } from '@studio/js-utils/time';
import { clsx } from 'cn';
import { take } from 'lodash-es';
import { useState } from 'react';
import type { ReactElement } from 'react';
import { twMerge } from 'cn';
import type { Notification } from '#~/notifications/index.js';
import { DataSTag, Sel, STagNew } from '#~/stag/index.js';
import { clear, close } from '#~/notifications/utils.js';
import T from '@studio/design-system/themes';

/** Notification with read flag. */
interface NotificationWithRead extends Notification {
  /** IsRead flag. */
  isRead: boolean;
}

interface Props {
  notifications: NotificationWithRead[];
}

const NotificationsList = ({
  notifications
}: {
  notifications: NotificationWithRead[];
}): ReactElement => {
  const [count, setCount] = useState(10);

  if (notifications.length === 0) {
    return <h2 className='text-muted'>No notifications present.</h2>;
  }

  return (
    <div className={twMerge('flex max-h-[min(720px_,_calc(100vh_-_150px))] flex-col gap-12')}>
      {take(notifications, count).map((notification) => (
        <motion.div key={notification.id} {...Motions.Fade} layout='position'>
          <Notice
            className='rounded-12 drop-shadow-sm transition-[all] hover:drop-shadow-lg'
            closeable={false}
            actions={
              String(notification.id).startsWith('alert:n6:') ? (
                <Button
                  size='sm'
                  variant='secondary'
                  {...STagNew(Sel.Notifications, 'clear_saved', String(notification.id))}
                  onClick={(): void => {
                    // Clear is explicit removal of saved history. If its popup
                    // is still open, the existing lifecycle records one dismissal.
                    close(notification.id, 'dismiss');
                    clear(notification.id);
                  }}
                >
                  Clear
                </Button>
              ) : undefined
            }
            // STag lets the browser layout spec find each card
            // (rule: playwright-stag-selectors-only, ai.knowledge/platform).
            {...DataSTag(Sel.Notifications, 'tray_notice', String(notification.id))}
            notification={{
              ...notification,
              // Body colour for the tray; the shared Notice owns wrapping, the
              // standard two-line size and the Show more toggle.
              // A block wrapper also accepts message components containing recovery buttons.
              message: <div className='text-secondary'>{notification.message}</div>
            }}
            meta={<span className='text-muted'>{formatNow(notification.timestamp)}</span>}
            unRead={!notification.isRead}
          />
        </motion.div>
      ))}
      {notifications.length > count && (
        <Button
          className='self-center p-8 text-foreground'
          data-theme={T.getTheme('light')}
          onClick={(): void => {
            setCount((c) => c + 10);
          }}
          size='lg'
          variant='secondary'
        >
          Show {Math.min(notifications.length - count, 10)} More
        </Button>
      )}
    </div>
  );
};

/**
 * Notification Tray Component.
 *
 * Bug 3 (spec §3.3): the pinned Multi-Add "needs configuration" card and
 * its `BatchNotificationRow`/`onRowActivate` machinery were removed — its
 * action was a verified no-op and it left the bell badge stuck. The
 * notification-centre list item (rendered by `NotificationsList` below,
 * carrying the real deep-link verb from the observer hook's `post()`) is
 * now the single surface, so the All/Unread tabs are the first element in
 * the panel.
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global).
 */
export const NotificationTray = ({ notifications }: Props): ReactElement => {
  const allNotifications = notifications;
  const unreadNotifications = notifications.filter(({ isRead }) => !isRead);

  return (
    <div
      // a11y: panel exposes a labelled region so screen readers announce
      // "Multi-Add notifications, region" when focus enters the popover.
      aria-label='Multi-Add notifications'
      // Bug 3 (spec §3.3.4): bound the tray height and scroll internally so
      // a high notification count never pushes the All/Unread tabs out of
      // the viewport (the "tabs unreachable" half of the report). The
      // stable testid lets the test assert the bound is present.
      //
      // whitespace-normal: the tray renders inside the masthead's right section,
      // which sets `whitespace-nowrap` (header.tsx) and is not portaled, so every
      // notice inherited `nowrap`. Each body then ran as one line past the 400px
      // card and the tab panel's overflow-x-hidden cut it off. `break-words` in
      // NotificationsList cannot help under `nowrap`. The tray sets its own width,
      // so it also resets text flow here instead of depending on its host.
      // Tab triggers and buttons keep their own `whitespace-nowrap`.
      // Per `reference-rules-in-comments` (ai.knowledge/global).
      className={twMerge(
        'w-[400px] whitespace-normal rounded-8 bg-light p-16',
        'max-h-[70vh] overflow-auto'
      )}
      data-testid='multi-add-notification-tray'
      {...DataSTag(Sel.Notifications, 'tray')}
      data-theme={T.getTheme('light')}
      role='region'
    >
      {/* Explains what the bell surfaces so a first-time user understands it flags
          alerts that need attention, not all history. Spec §04. */}
      <p className='mb-12 text-sm text-muted'>Bell shows unread alerts that need attention.</p>

      <Tabs defaultValue='all'>
        <List>
          <Trigger className='text-black' value='all'>
            All <Count count={allNotifications.length} />
          </Trigger>
          <Trigger
            className={clsx({
              'text-black': unreadNotifications.length > 0
            })}
            disabled={unreadNotifications.length === 0}
            value='unread'
          >
            Unread {unreadNotifications.length > 0 && <Count count={unreadNotifications.length} />}
          </Trigger>
        </List>

        <div className='mt-16'>
          <Content value='all'>
            <NotificationsList notifications={allNotifications} />
          </Content>
          <Content value='unread'>
            <NotificationsList notifications={unreadNotifications} />
          </Content>
        </div>
      </Tabs>
    </div>
  );
};
